import { ItemPreferences } from '@admin/models';
import { ItemPreferencesWebService } from '@admin/web-services';
import { Injectable } from '@angular/core';
import { ItemResource, Link } from '@ngx-hal/models';
import { ItemProfileRead } from '@ngx-hal/states';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ItemPreferencesPatch, ItemPreferencesRead, ItemPreferencesUpdateSample } from './item-preferences.actions';

type ItemPreferencesStateModel<Entity extends ItemResource> =
  | Map<Link['href'], ItemPreferences<Entity> | undefined>
  | undefined;

@State<ItemPreferencesStateModel<ItemResource>>({
  name: 'itemPreferences',
  defaults: null as unknown as undefined,
})
@Injectable()
export class ItemPreferencesState<Entity extends ItemResource> {
  static read<Entity extends ItemResource>(link: Link) {
    return createSelector([ItemPreferencesState], (state: ItemPreferencesStateModel<Entity>) => state?.get(link.href));
  }

  constructor(private readonly itemPreferencesWebService: ItemPreferencesWebService<Entity>) {}

  @Action(ItemProfileRead)
  @Action(ItemPreferencesRead)
  doRead(
    context: StateContext<ItemPreferencesStateModel<Entity>>,
    { link }: ItemProfileRead | ItemPreferencesRead,
  ): Observable<unknown> {
    return this.itemPreferencesWebService.read(link).pipe(
      map(response => new Map(context.getState()?.entries() ?? []).set(link.href, response)),
      map(context.setState),
      catchError(error => (error.status === 404 ? EMPTY : throwError(error))),
    );
  }

  @Action(ItemPreferencesPatch)
  doPatch(
    context: StateContext<ItemPreferencesStateModel<Entity>>,
    { link, itemPreferences }: ItemPreferencesPatch<Entity>,
  ): Observable<unknown> {
    return this.itemPreferencesWebService.patch(link, itemPreferences).pipe(
      map(response => new Map(context.getState()?.entries() ?? []).set(link.href, response)),
      map(context.setState),
    );
  }

  @Action(ItemPreferencesUpdateSample)
  doUpdateSample(
    context: StateContext<ItemPreferencesStateModel<Entity>>,
    { link, sample }: ItemPreferencesUpdateSample<Entity>,
  ): Observable<unknown> {
    return this.itemPreferencesWebService.read(link).pipe(
      catchError(error => (error.status === 404 ? of(undefined) : throwError(error))),
      map(response => ({
        ...(response ?? undefined),
        ...(response?.list
          ? {
              list: response.list._page
                ? { ...response.list, _page: { ...response.list._page, page: undefined } }
                : response.list,
            }
          : {}),
        sample,
      })),
      switchMap(itemPreferences => this.itemPreferencesWebService.update(link, itemPreferences)),
      map(response => new Map(context.getState()?.entries() ?? []).set(link.href, response)),
      map(context.setState),
    );
  }
}
